import { EpisodeReadRecordDto } from '@diritto/common/recentlyRead/dtos/episdoe-read-record.dto';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { AdultBadgeIcon } from 'icons/badge/AdultBadgeIcon';
import { UpBadgeIcon } from 'icons/badge/UpBadgeIcon';
import { VerticalDivider } from 'icons/VerticalDivider';
import React, { useCallback, useEffect, useRef } from 'react';
import { useEpisodeState } from 'state-hooks/episode/useEpisodeState';
import { getTextLineStyle } from 'styles/common';
import { Divider } from 'ui-components/common/Divider';
import { DotDivider } from 'ui-components/common/DotDivider';
import { Link } from 'ui-components/common/Link';
import { RelativeDateText } from 'ui-components/common/RelativeDateText';
import { IconCount, IconCountType } from 'ui-components/contents/IconCount';

type ContentDetailEpisodeItemProps = {
  volume?: number;
  episode: EpisodePublicRelayDto;
  readEpisode?: EpisodeReadRecordDto;
  replyCount?: number;
  recommendCount?: number;
  isAdultContent: boolean;
  isSelected: boolean;
  replaceLink: boolean;
  onClick?: () => void;
};

export const ContentDetailEpisodeItem = ({
  volume,
  episode,
  readEpisode,
  replyCount,
  recommendCount,
  isAdultContent,
  isSelected,
  replaceLink,
  onClick,
}: ContentDetailEpisodeItemProps) => {
  const { isAdult, isUpdated } = useEpisodeState(episode, isAdultContent);

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSelected) {
      ref?.current?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
    }
  }, [isSelected]);

  const handleOnClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  return (
    <Link href={`/contents/${episode.contentId}/episodes/${episode.id}`} replace={replaceLink} onClick={handleOnClick}>
      <StyledContainer ref={ref} isSelected={isSelected}>
        <StyledWrapper read={!isSelected && !!readEpisode}>
          <StyledOrderNum>{volume ?? '-'}</StyledOrderNum>
          <div>
            <StyledTitleWrapper>
              <StyledBadgeWrapper>
                {isUpdated && <UpBadgeIcon />}
                {isAdult && <AdultBadgeIcon />}
              </StyledBadgeWrapper>
              <p>{episode.title}</p>
            </StyledTitleWrapper>
            <StyledMetaWrapper>
              {episode.firstOpenedAt && <RelativeDateText date={episode.firstOpenedAt} />}
              <StyledVerticalDividerWrapper>
                <VerticalDivider />
              </StyledVerticalDividerWrapper>
              <StyledIconCount
                type={IconCountType.View}
                count={episode.counts.viewCount}
                color={Color.Gray400}
                iconClassName="size-[16px]"
              />
              <DotDivider />
              <StyledIconCount
                type={IconCountType.Recommend}
                count={recommendCount ?? 0}
                color={Color.Gray400}
                iconClassName="size-[16px]"
              />
              <DotDivider />
              <StyledIconCount
                type={IconCountType.Reply}
                count={replyCount ?? 0}
                color={Color.Gray400}
                iconClassName="size-[16px]"
              />
            </StyledMetaWrapper>
          </div>
        </StyledWrapper>
        <StyledDivider position="bottom" color={Color.Gray200} />
      </StyledContainer>
    </Link>
  );
};

const StyledContainer = styled.div(
  ({ isSelected = false }: { isSelected?: boolean }) => css`
    background-color: ${isSelected ? Color.Gray100 : 'transparent'};

    &:hover {
      background: ${Color.Gray200}80;
    }

    position: relative;
    padding: 16px;
  `,
);

const StyledWrapper = styled.div(
  ({ read = false }: { read?: boolean }) => css`
    opacity: ${read ? 0.5 : 1};
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
);

const StyledOrderNum = styled.div`
  width: 30px;
  flex-shrink: 0;
  margin-right: 8px;

  ${Typography.XXSmall}
  font-weight: ${FontWeight.Medium};
  color: ${Color.Gray600};
`;

const StyledIconCount = styled(IconCount)`
  height: 16px;
  & > p {
    color: ${Color.Gray600};
  }
`;

const StyledTitleWrapper = styled.div(
  ({ isSelected = false }: { isSelected?: boolean }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 24px;
    margin-bottom: 6px;

    & > p {
      color: ${Color.Black};
      font-weight: ${isSelected ? FontWeight.Bold : FontWeight.Medium};
      ${getTextLineStyle(1)}
    }
  `,
);

const StyledBadgeWrapper = styled.div`
  display: flex;
  align-items: center;

  & > svg {
    flex-shrink: 0;

    &:last-of-type {
      margin-right: 6px;
    }
  }

  svg + svg {
    margin-left: 2px;
  }
`;

const StyledMetaWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${Color.Gray600};
  gap: 4px;

  ${Typography.XXXSmall}
`;

const StyledDivider = styled(Divider)`
  left: 16px;
  width: calc(100% - 32px);
  bottom: 0;
`;

const StyledVerticalDividerWrapper = styled.div`
  margin: 0 4px;
`;
